import React from 'react'
import * as Yup from 'yup'
import Ajax from '../../../Ajax'
import { useFormik } from "formik"
import Field from '../fields/Field'
import { Button, CircularProgress } from "@mui/material"
import { store } from "../../../stores/Store"

class SamlSettings {
    tenantId: string = "";
    provider: string = "";

    entityId: string = "";
    replyUrl: string = "";
    certificate: string = "";
}

class OAuthSettings {
    tenantId: string = "";
    provider: string = "";

    clientId: string = "";
    secret: string = "";
    redirectUri: string = "";
}

class AuthSettings {
    accountUid: string = "";
    accountHostname: string = "";
    allowPasswordOnly: boolean = true;
    allow2faWithEmail: boolean = false;
    allow2faWithSms: boolean = false;
    allowDelegatedAuthentication: boolean = false;
    allowDelegatedAuthWithOauth: boolean = false;
    allowDelegatedAuthWithSaml: boolean = false;
    samlSettings: SamlSettings = null;
    oAuthSettings: OAuthSettings = null
    updatedAt?: string = null;
    updatedBy?: string = null;
}


export default function AccountAuthConfigForm ({account}) {

    const [settings, setSettings] = React.useState<AuthSettings>(null)

    React.useEffect(() => {
        Ajax.Account.GetAuthSettings(account.uid).then((response) => {
            setSettings(response.data)
        }).catch((response) => {
            setSettings(new AuthSettings())
        })
    }, [])

    if (!settings) {
        return (
            <div style={{width: "280px", textAlign: "center", margin: "10px"}}>
                <CircularProgress />
            </div>
        )
    }

    return <AuthForm accountUid={account.uid} settings={settings} />
}

interface AuthFormProps {
    accountUid: string;
    settings: AuthSettings;
}

function AuthForm ({accountUid, settings}: AuthFormProps) {

    const initialSamlSettings: SamlSettings = settings.samlSettings
    const initialOauthSettings: OAuthSettings = settings.oAuthSettings

    const formatReplyUrl = (hostname) => {
        return `https://${hostname}/api/session/saml-reply`
    }

    var formik = useFormik({

        initialValues: {
            accountUid: accountUid,
            accountHostname: settings.accountHostname,

            // Converted to select field
            allowPasswordOnly: settings.allowPasswordOnly,
            allow2faWithEmail: settings.allow2faWithEmail,
            allow2faWithSms: settings.allow2faWithSms,
            allowDelegatedAuthentication: settings.allowDelegatedAuthentication,
            allowDelegatedAuthWithOauth: settings.allowDelegatedAuthWithOauth,
            allowDelegatedAuthWithSaml: settings.allowDelegatedAuthWithSaml,

            // Flattened things shared in initialSamlSettings and initialOauthSettings
            provider: "microsoft",
            tenantId: initialSamlSettings?.tenantId ? initialSamlSettings?.tenantId : initialOauthSettings?.tenantId,

            // Flattened things from initialSamlSettings
            entityId: initialSamlSettings?.entityId,
            replyUrl: formatReplyUrl(settings.accountHostname),
            certificate: initialSamlSettings?.certificate,

            // Flattened things from initialOauthSettings
            clientId: initialOauthSettings?.clientId,
            secret: initialOauthSettings?.secret,
            redirectUri: `https://${settings.accountHostname}`,
        },

        validationSchema: Yup.object({
            accountHostname: Yup.string().matches(/^([a-z0-9\-_]*\.aoralaw.com)$/, "Must be a valid hostname").required("A custom hostname is required when using SAML."),
            allowPasswordOnly: Yup.bool().required(),
            allow2faWithEmail: Yup.bool().required(),
            allow2faWithSms: Yup.bool().required(),
            allowDelegatedAuthentication: Yup.bool().required(),
            allowDelegatedAuthWithOauth: Yup.bool().required(),
            allowDelegatedAuthWithSaml: Yup.bool().required(),

            tenantId: Yup.string().when("allowDelegatedAuthentication", {
                is: (allowDelegatedAuthentication) => allowDelegatedAuthentication,
                then: () => Yup.string().required(),
                otherwise: () => Yup.string().nullable()
            }),

            // SAML
            entityId: Yup.string().when("allowDelegatedAuthWithSaml", {
                is: (allowDelegatedAuthWithSaml) => allowDelegatedAuthWithSaml,
                then: () => Yup.string().required(),
                otherwise: () => Yup.string().nullable()
            }),
            certificate: Yup.string().when("allowDelegatedAuthWithSaml", {
                is: (allowDelegatedAuthWithSaml) => allowDelegatedAuthWithSaml,
                then: () => Yup.string().required(),
                otherwise: () => Yup.string().nullable()
            }),

            // OAuth
            clientId: Yup.string().when("allowDelegatedAuthWithOauth", {
                is: (allowDelegatedAuthWithOauth) => allowDelegatedAuthWithOauth,
                then: () => Yup.string().required(),
                otherwise: () => Yup.string().nullable()
            }),
            secret: Yup.string().when("allowDelegatedAuthWithOauth", {
                is: (allowDelegatedAuthWithOauth) => allowDelegatedAuthWithOauth,
                then: () => Yup.string().required(),
                otherwise: () => Yup.string().nullable()
            }),
            redirectUri: Yup.string().when("allowDelegatedAuthWithOauth", {
                is: (allowDelegatedAuthWithOauth) => allowDelegatedAuthWithOauth,
                then: () => Yup.string().required(),
                otherwise: () => Yup.string().nullable()
            }),
        }),

        onSubmit: async (values) => {

            var valuesToSend: AuthSettings = {
                accountUid: values.accountUid,
                accountHostname: values.accountHostname,
                allowPasswordOnly: values.allowPasswordOnly,
                allow2faWithEmail: values.allow2faWithEmail,
                allow2faWithSms: values.allow2faWithSms,
                allowDelegatedAuthentication: values.allowDelegatedAuthentication,
                allowDelegatedAuthWithOauth: values.allowDelegatedAuthWithOauth,
                allowDelegatedAuthWithSaml: values.allowDelegatedAuthWithSaml,
                samlSettings: null,
                oAuthSettings: null,
            }

            if (values.allowDelegatedAuthWithSaml) {
                valuesToSend = {
                    ...valuesToSend,
                    samlSettings: {
                        // hostname: values.accountHostname,
                        provider: values.provider,
                        tenantId: values.tenantId,

                        entityId: values.entityId,
                        replyUrl: formatReplyUrl(values.accountHostname),
                        certificate: values.certificate
                    }
                }
            }

            if (values.allowDelegatedAuthWithOauth) {
                valuesToSend = {
                    ...valuesToSend,
                    oAuthSettings: {
                        // hostname: values.accountHostname,
                        provider: values.provider,
                        tenantId: values.tenantId,

                        clientId: values.clientId,
                        secret: values.secret,
                        redirectUri: values.redirectUri,
                    }
                }
            }

            await Ajax.Account.UpdateAuthSettings(valuesToSend).then(() => {
                store.AlertStore.Close()
            }).catch((response) => {
                store.AlertStore.Alert({message: response.data?.detail})
            })
        }
    })

    var disabled = !store.AppStore.checkJwtRoles(["sysuser"])

    return (
        <form autoComplete="off" className={disabled ? "form disabled" : "form"} onSubmit={formik.handleSubmit}>
            <h2>Authentication Settings</h2>

            <div style={{display: "flex", gap: "20px"}}>
                <div style={{width: "280px"}}>
                    <Field.Text
                        formik
                        name="accountHostname"
                        warning={formik.submitCount && formik.errors.accountHostname ? formik.errors.accountHostname : ""}
                        value={formik.values.accountHostname}
                        label="Hostname"
                        onChange={(e) => {
                            formik.handleChange(e)
                            formik.setFieldValue("replyUrl", formatReplyUrl(e.target.value))
                            formik.setFieldValue("redirectUri", `https://${e.target.value}`)
                        }} />

                    <Field.Switch 
                        formik 
                        disabled={formik.values.allow2faWithEmail || formik.values.allow2faWithSms || formik.values.allowDelegatedAuthentication}
                        value={formik.values.allowPasswordOnly} 
                        name="allowPasswordOnly" 
                        label="Password" 
                        onChange={(e) => {
                            formik.handleChange(e)
                        }} />
                    <Field.Switch 
                        formik 
                        value={formik.values.allow2faWithEmail} 
                        name="allow2faWithEmail" 
                        label="Password with 2FA email" 
                        onChange={(e) => {
                            if (e.target.value === "false") {
                                formik.setFieldValue("allowPasswordOnly", false)
                            }
                            formik.handleChange(e)
                        }} />
                    <Field.Switch 
                        formik 
                        value={formik.values.allow2faWithSms} 
                        name="allow2faWithSms" 
                        label="Password with 2FA SMS" 
                        onChange={(e) => {
                            if (e.target.value === "false") {
                                formik.setFieldValue("allowPasswordOnly", false)
                            }
                            formik.handleChange(e)
                        }} />
                    
                    <Field.Switch 
                        formik 
                        value={formik.values.allowDelegatedAuthentication} 
                        name="allowDelegatedAuthentication" 
                        label="Delegated authentication" 
                        onChange={(e) => {
                            if (e.target.value === "false") {
                                formik.setFieldValue("allowDelegatedAuthWithSaml", false)
                                formik.setFieldValue("allowDelegatedAuthWithOauth", false)
                                formik.setFieldValue("allowPasswordOnly", false)
                            }

                            formik.handleChange(e)
                        }} />
                    
                    {formik.values.allowDelegatedAuthentication ? (
                        <>
                            <Field.Radio 
                                formik 
                                value={formik.values.allowDelegatedAuthWithSaml ? "saml" : "oauth"} 
                                name="allowDelegatedAuthWithSaml" 
                                label="How would you like to delegate authentication?"
                                options={[
                                    {value: "saml", label: "Microsoft SAML"},
                                    {value: "oauth", label: "Microsoft OAuth"},
                                ]}
                                onChange={(e) => {
                                    if (e.target.value === "oauth") {
                                        formik.setFieldValue("allowDelegatedAuthWithSaml", false)
                                        formik.setFieldValue("allowDelegatedAuthWithOauth", true)
                                    }
                                    if (e.target.value === "saml") {
                                        formik.setFieldValue("allowDelegatedAuthWithSaml", true)
                                        formik.setFieldValue("allowDelegatedAuthWithOauth", false)
                                    }
                                }} />
                        </>
                    ) : null}
                </div>

                {formik.values.allowDelegatedAuthWithSaml ? (
                    <div style={{width: "330px"}}>
                        <SamlForm formik={formik} />
                    </div>
                ) : null}

                {formik.values.allowDelegatedAuthWithOauth ? (
                    <div style={{width: "330px"}}>
                        <OauthForm formik={formik} />
                    </div>
                ) : null}
            </div>

            <div className="form-buttons">
                <Button
                    color="primary"
                    className="btn btn-sm"
                    onClick={() => store.AlertStore.Close()}>
                    Cancel
                </Button>
                <Button
                    type="submit"
                    color="success"
                    variant="contained"
                    className="btn btn-sm"
                    disabled={formik.isSubmitting}>
                    Submit
                </Button>
            </div>
        </form>
    )
}


function SamlForm ({formik}) {
    return (
        <div className="form-fields">
            <Field.Text
                formik
                name="tenantId"
                warning={formik.submitCount && formik.errors.tenantId ? formik.errors.tenantId : false}
                value={formik.values.tenantId}
                label="Tenant ID"
                onChange={formik.handleChange} />

            <Field.Text
                formik
                name="entityId"
                warning={formik.submitCount && formik.errors.entityId ? formik.errors.entityId : false}
                value={formik.values.entityId}
                label="Entity ID"
                onChange={formik.handleChange} />

            <Field.Text
                formik
                name="certificate"
                warning={formik.submitCount && formik.errors.certificate ? formik.errors.certificate : false}
                value={formik.values.certificate}
                label="Certificate"
                rows={5}
                onChange={formik.handleChange} />

            <div>
                <label className="help-text">Reply URL</label>
                <p style={{overflowWrap: "break-word"}}>{formik.values.replyUrl ? formik.values.replyUrl : "Generated using 'Hostname'"}</p>
            </div>
        </div>
    )
}

function OauthForm ({formik}) {
    return (
        <div className="form-fields">
            <Field.Text
                formik
                name="tenantId"
                warning={formik.submitCount && formik.errors.tenantId ? formik.errors.tenantId : false}
                value={formik.values.tenantId}
                label="Tenant ID"
                onChange={formik.handleChange} />

            <Field.Text
                formik
                name="clientId"
                warning={formik.submitCount && formik.errors.clientId ? formik.errors.clientId : false}
                value={formik.values.clientId}
                label="clientId"
                onChange={formik.handleChange} />
            
            <Field.Text
                formik
                name="secret"
                warning={formik.submitCount && formik.errors.secret ? formik.errors.secret : false}
                value={formik.values.secret}
                label="secret"
                helpText="Existing secrets will not be displayed here, this field is write-only."
                onChange={formik.handleChange} />
            
            <div>
                <label className="help-text">Redirect URL</label>
                <p style={{overflowWrap: "break-word"}}>{formik.values.redirectUri ? formik.values.redirectUri : "Generated using 'Hostname'"}</p>
            </div>
        </div>
    )
}